/* eslint-disable check-file/filename-blocklist */
import type { IntlShape } from 'react-intl-next';

import type { ADNode } from '@atlaskit/editor-common/validator';
import type { EditorActions } from '@atlaskit/editor-core';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { ExtensionModule } from '@atlaskit/editor-common/extensions';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';

import { cfetch } from '@confluence/network';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { fg } from '@confluence/feature-gating';
import { getMacroId } from '@confluence/macro-tracker/entry-points/getExtensionKey';

import type { ExtensionNodeType } from '../../extensions-common/extensionNodeTypes';
import { isBodiedExtension } from '../../extensions-common/extensionNodeTypes';
import { allowedMacrosForInsertMenu } from '../../extensionConstants';
import type { MacroConfig } from '../../providers/macros/extension-provider-types';

import type {
	LegacyMacroManifest,
	ConfluencePageContext,
	OnCompleteCallbackFnType,
} from './extensionTypes';
import { safeGetMacroName } from './manifest-helpers';
// eslint-disable-next-line import/no-cycle
import { getQuickInsertAction } from './quick-insert-action';
import { getIconComponent } from './MacroComponents';

const pageIdRegex = /^[0-9]+$/;
export const isPageId = (id: string) => pageIdRegex.test(id);

// Temporary solution until we have `extensionAPI.doc.getSelectedNode` @see ED-13045
export const getSelectedBodiedExtensionNode = (editorActions?: EditorActions) => {
	const editorView = editorActions?._privateGetEditorView();
	if (editorView) {
		let maybeNode = (editorView.state.selection as any).node;
		if (!maybeNode || !isBodiedExtension(maybeNode.type.name)) {
			const { $head } = editorView.state.selection;
			// If current selection isn't bodied extension traverse upward
			for (let i = $head.depth; i > 0; i--) {
				const node = $head.node(i);
				if (isBodiedExtension(node.type.name as ExtensionNodeType)) {
					maybeNode = node;
				}
			}
		}
		return maybeNode?.toJSON() as ADNode;
	}
};

export const isValueSet = (value: any) =>
	(value === 0 || value === false || !!value) && (!Array.isArray(value) || value.length > 0);

export const createQuickInsertModule = (
	macro: LegacyMacroManifest,
	pageContext: ConfluencePageContext,
	openMacroBrowserForInitialConfiguration: any,
	editorAPI: PublicPluginAPI<[ExtensionPlugin]> | undefined,
	intl: IntlShape,
	macroBrowserConfig?: MacroConfig,
	editorActions?: EditorActions,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	onCompleteCallback?: OnCompleteCallbackFnType,
	isLivePage?: boolean,
	isLivePagesFeatureEnabled?: boolean,
): ExtensionModule[] => {
	// Hidden macros don't appear in the slash menu
	// but still need to be defined in a manifest so old pages can show them
	if (macro.hidden) {
		return [];
	}

	const macroName = safeGetMacroName(macro);

	try {
		const action = getQuickInsertAction(
			macro,
			pageContext,
			openMacroBrowserForInitialConfiguration,
			editorAPI,
			intl,
			macroBrowserConfig,
			editorActions,
			createAnalyticsEvent,
			onCompleteCallback,
			isLivePage,
			isLivePagesFeatureEnabled,
		);

		return [
			{
				key: macroName,
				title: macro.title,
				description: macro.description,
				keywords: [macro.macroName, ...macro.aliases],
				categories: macro.categories,
				icon: async () => () => getIconComponent(macro),
				featured:
					pageContext.features.elementBrowser && allowedMacrosForInsertMenu.includes(macroName),
				action,
			},
		];
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
		return [];
	}
};

// Extract App Key from moduleKey for connect app
// com.atlassian.plugins.atlassian-connect-plugin:xhtml__cc-eco-test-connect-multi-bodied-extension__cc-eco-test-multi-bodied-extension
// ====>
// cc-eco-test-connect-multi-bodied-extension
export const getConnectAppKey = (macroKey?: string | null): string => {
	if (!macroKey || !macroKey.startsWith('com.atlassian.plugins.atlassian-connect-plugin')) {
		return '';
	}

	const chunks = macroKey.split('__');
	if (chunks.length !== 3) {
		return '';
	}

	return chunks[1];
};

export const insertMacroPlaceholder = (node, placeholderText: string) => {
	node.content = [
		{
			type: 'paragraph',
			content: [
				{
					type: 'placeholder',
					attrs: {
						text: placeholderText,
					},
				},
			],
		},
	];
};

export const insertContentPropertiesMacroPlaceholder = (
	node,
	keyPlaceholderHeading: string,
	valuePlaceholderHeading: string,
) => {
	node.content = [
		{
			type: 'table',
			attrs: {
				isNumberColumnEnabled: false,
				layout: 'default',
				localId: getMacroId(null),
				width: 760,
			},
			content: [
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [
										{
											type: 'placeholder',
											attrs: {
												text: keyPlaceholderHeading,
											},
										},
									],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {
								background: '#ffffff',
							},
							content: [
								{
									type: 'paragraph',
									content: [
										{
											type: 'placeholder',
											attrs: {
												text: valuePlaceholderHeading,
											},
										},
									],
								},
							],
						},
					],
				},
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {
								background: '#f4f5f7',
							},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
					],
				},
				{
					type: 'tableRow',
					content: [
						{
							type: 'tableHeader',
							attrs: {
								background: '#f4f5f7',
							},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
						{
							type: 'tableCell',
							attrs: {},
							content: [
								{
									type: 'paragraph',
									content: [],
								},
							],
						},
					],
				},
			],
		},
	];
};

const getMatchingAppLinkForViewPage = async (selectedServerId: string) => {
	try {
		const res = await cfetch('/wiki/rest/jiraanywhere/1.0/servers');
		const appLinks: { url: string; rpcUrl: string; id: string }[] = await res.json();

		return appLinks.find((appLink) => appLink.id === selectedServerId);
	} catch {}
};

const isCloudAppLinkFromUrl = (url?: string) => {
	if (!url) {
		return;
	}

	return url.includes('atlassian.net') || url.includes('jira-dev.com');
};

export const getJiraHostingOption = async (
	macroName: string,
	selectedServerId: string = '',
	isViewPage: boolean = false,
) => {
	if (macroName !== 'jira') {
		return;
	}

	let isCloudAppLink: boolean | undefined;

	if (isViewPage) {
		const selectedServerObject = await getMatchingAppLinkForViewPage(selectedServerId);

		isCloudAppLink =
			isCloudAppLinkFromUrl(selectedServerObject?.url) ||
			isCloudAppLinkFromUrl(selectedServerObject?.rpcUrl);
	} else {
		try {
			// module is only available on edit page
			const ApplinksBridge = window.require('confluence/jim/jira/applinks-bridge-for-spa');

			isCloudAppLink = await ApplinksBridge.isCloudAppLinkFromServerId(selectedServerId);
		} catch {}
	}

	if (isCloudAppLink === undefined) {
		return 'unknown';
	}

	return isCloudAppLink ? 'cloud' : 'server';
};

export const filterOutHiddenMacros = (
	macros: any,
	hideMacrosOptions?: { isLivePage?: boolean; isCompanyHub?: boolean },
) => {
	const newMacros: any[] = [];
	const macrosToHideLivePage = new Set(
		StatsigConfigurations.getStringList('confluence_frontend_hide_selected_macros', []),
	);
	const macrosToHideCompanyHub = new Set(['toc', 'recently-updated', 'iframe']);

	for (const macro of macros) {
		if (hideMacrosOptions?.isCompanyHub && macrosToHideCompanyHub.has(macro?.macroName)) {
			// Create a copy so we don't mutate the original macro
			const hiddenMacro = { ...macro, hidden: true };
			newMacros.push(hiddenMacro);
		} else if (hideMacrosOptions?.isLivePage && macrosToHideLivePage.has(macro?.macroName)) {
			// Create a copy so we don't mutate the original macro
			const hiddenMacro = { ...macro, hidden: true };
			newMacros.push(hiddenMacro);
		} else {
			newMacros.push(macro);
		}
	}
	return newMacros;
};

// When node has no macroId, will generate one and update doc with it. Otherwise returns currentNode as is
export const getAndUpdateNodeWithMacroId = (
	currentNode: ADNode,
	actions: any,
	editorActions?: EditorActions,
) => {
	if (!fg('confluence_frontend_generate_missing_macro_id')) {
		return currentNode;
	}

	// Using this editor actions node instead since it has macroId the original selectedNode doesn't have
	const selectedNodeWithMoreInfo = editorActions?.getSelectedNode?.();
	const hasMacroId = !!selectedNodeWithMoreInfo?.attrs?.parameters?.macroMetadata?.macroId;

	// macroId is missing from the macro data returned above in selectedNode for drafts (live pages & classic editor draft)
	// The storage format has the macroId which is generated via BE but FE isn't reflecting this data in ADF.
	// BE will keep regenerating the id until some page action (e.g publish, copy, conversions between content types)
	// In the case where there is no macroId, we explcitly generate one on FE so BE doesn't keep getting regenerating.
	// This regeneration was causing issues for vendors, as they call REST APIs for their macros which rely on a stable macroId to properly fetch.
	const macroId = selectedNodeWithMoreInfo?.attrs?.parameters?.macroMetadata?.macroId || {
		value: getMacroId(selectedNodeWithMoreInfo?.attrs?.parameters),
	};

	// Spreading into currentNode since selectedNodeWithMoreInfo has extra node info and can't be used directly with existing macro browser logic
	const newNode = {
		...currentNode,
		attrs: {
			...currentNode.attrs,
			parameters: {
				...currentNode.attrs.parameters,
				macroMetadata: {
					...currentNode?.attrs?.parameters?.macroMetadata,
					macroId,
				},
			},
		},
	};

	if (!hasMacroId) {
		const localId = selectedNodeWithMoreInfo?.attrs?.localId;
		// Update node in editor to reflect the new macroId so BE can persist
		actions?.doc?.update(localId, () => newNode);
	}

	return newNode;
};
